import React, { useEffect, useState } from 'react';
import Header from '@custom/Header/Header';
import { DatePickerWithRange } from '@ohif/ui-next';
import { CustomTable, InputMultiSelect, Loader, SuccessModal } from '@ohif/ui';
import i18n from '@ohif/i18n';
import { customStyle, formatDate } from '@custom/Utils/utils';
import { useDispatch, useSelector } from 'react-redux';
import listStudyResearch from '../rtk-apis/studyResearch/listStudyResearch';
import updateStudyResearch from '../rtk-apis/studyResearch/updateStudyResearch';
import { useNavigate } from 'react-router-dom';
import { _roleType, _studyResearchTableColumns } from '@custom/Utils/constants/appConstants';
import { closeModal } from '@custom/store/slices/StudyResearch/updateStudyResearchSlice';
import { servicesManager } from '../../app/src/App';
import getDateFormat from '../../ui/src/utils/getDateFormat';
import { apiRequest } from '@custom/Utils/config/apiRequest';
import { _commonUrls } from '@custom/Utils/constants/apiUrls';
import { useTheme } from "../../ui/src/contextProviders"

const StudyResearch = () => {

  const [showTable, setShowTable] = useState(false);
  const [filters, setFilters] = useState({
    patientName: '',
    patientId: '',
    studyDate: { startDate: '', endDate: '' },
    modality: [],
    study: '',
    diagnosis: ''
  });
  const [researchData, setResearchData] = useState({
    patientName: '',
    patientId: '',
    studyDate: '',
    clinicalHistory: '',
    radiologyFinding: '',
    cytologyFindings: '',
    diagnosis: '',
    followUp: '',
    discussionNotes: '',
    finalTreatment: ''
  })
  const [clickedItem, setClickedItem] = useState(null);
  const [successPopup, setSuccessPopup] = useState(false);
  const [modalities, setModalities] = useState([]);
  const { isDarkMode } = useTheme();


  const { uiNotificationService } = servicesManager.services;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const studyResearchData = useSelector((state) => state.studyResearchList);
  const updateStudyResearchResp = useSelector((state) => state.updateStudyResearch);

  const queryParams = new URLSearchParams(location.search);
  const refetch = queryParams.get('refetch');
  const role = sessionStorage.getItem('role') || localStorage.getItem('role');

  useEffect(() => {
    if (refetch) {
      const payload = { ...filters, studyDate: { startDate: convertDateFormat(filters.studyDate.startDate), endDate: convertDateFormat(filters.studyDate.endDate) } };
      setClickedItem(null);
      setShowTable(true);
      dispatch(listStudyResearch(payload));
    }
  }, [refetch]);

  const handleFilterChange = (event) => {
    setFilters((curr) => {
      if (event.isModality) {
        return { ...curr, modality: event.values };
      }

      if (event.isDatePicker) {
        return { ...curr, studyDate: event.values };
      }

      return { ...curr, [event.target.name]: event.target.value };
    })
  }

  const handleFilterClear = () => {
    setShowTable(false);
    setFilters({
      patientName: '',
      patientId: '',
      studyDate: { startDate: '', endDate: '' },
      modality: [],
      study: '',
      diagnosis: '',
    });
    setResearchData({
      patientName: '',
      patientId: '',
      studyDate: '',
      clinicalHistory: '',
      radiologyFinding: '',
      cytologyFindings: '',
      diagnosis: '',
      followUp: '',
      discussionNotes: '',
      finalTreatment: ''
    })
    setClickedItem(null);
  }

  const fetchCommonData = async () => {
    try {
      const commonData: any = await apiRequest({ method: 'POST', endUrl: _commonUrls.list, body: {}, headerType: 'json' })
      setModalities(commonData?.result?.modality);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchCommonData()
  }, [])

  function convertDateFormat(ddmmyyyy) {
    if (!ddmmyyyy) return "";
    const dd = ddmmyyyy.slice(0, 2);
    const mm = ddmmyyyy.slice(2, 4);
    const yyyy = ddmmyyyy.slice(4, 8);
    return `${yyyy}${mm}${dd}`;
  }

  const handleSearchClick = () => {
    const payload = { ...filters, studyDate: { startDate: convertDateFormat(filters.studyDate.startDate), endDate: convertDateFormat(filters.studyDate.endDate) } };
    setClickedItem(null);
    setShowTable(true);
    dispatch(listStudyResearch(payload));
  }

  const handleResearchDataChange = (event) => {
    setResearchData((curr) => {
      return { ...curr, [event.target.name]: event.target.value }
    })
  }

  const handleSaveClick = async () => {
    if (!clickedItem) {
      uiNotificationService.show({
        title: 'Warning',
        message: 'Please select any research data.',
        type: "error",
        position: "topRight"
      });
      return;
    }
    const { patientName, patientId, studyDate, ...updatedResearchData } = researchData;
    const payload = {
      studyResearchId: clickedItem?._id,
      ...updatedResearchData
    }
    dispatch(updateStudyResearch(payload));
  }

  const handleCancelClick = () => {
    navigate('/');
  }

  const handleViewerClick = () => {
    if (!(clickedItem?.studyInstanceUID || clickedItem?.wsiId)) {
      return;
    } else if(clickedItem?.studyInstanceUID){
      navigate(`/viewer?StudyInstanceUIDs=${encodeURIComponent(clickedItem?.studyInstanceUID)}`);
    }else if(clickedItem?.wsiId){
      navigate(`/WSI/${clickedItem?.uhid}/${clickedItem?.wsiId}`)
    }

  }

  useEffect(() => {
    setResearchData({
      patientName: clickedItem?.patientName || '',
      patientId: clickedItem?.patientId || '',
      studyDate: clickedItem?.studyDate || '',
      clinicalHistory: clickedItem?.clinicalHistory || '',
      radiologyFinding: clickedItem?.radiologyFinding || '',
      cytologyFindings: clickedItem?.cytologyFindings || '',
      diagnosis: clickedItem?.diagnosis || '',
      followUp: clickedItem?.followUp || '',
      discussionNotes: clickedItem?.discussionNotes || '',
      finalTreatment: clickedItem?.finalTreatment || '',
    });

  }, [clickedItem])

  useEffect(() => {
    if (updateStudyResearchResp?.status === 'SUCCESS') {
      setSuccessPopup(true);

      setTimeout(() => {
        dispatch(listStudyResearch(filters));
      }, 800);
    }
    else if (updateStudyResearchResp?.status === 'ERROR' || updateStudyResearchResp?.status === 'failure') {
      uiNotificationService.show({
        title: 'Error',
        message: updateStudyResearchResp?.msg,
        type: "error",
        position: "topRight"
      });
    }
    dispatch(closeModal());
  }, [updateStudyResearchResp])

  return (
    <>
      <div className='page page__full' style={
        isDarkMode ? { background: '#090c29' } : customStyle.mainBackground
      }>
        <Header title={'Study Research'} isBack={false} />
        <div className='page__body'>
          <div className='details'>
            <div className='form form-innersec'>
              <section className='form__section'>
                <div className='formRow formRow__3'>
                  <div className='formCol'>
                    <div className="form__Field">
                      <label className="form__label" htmlFor='name'>Patient Name</label>
                      <input className="form__input form__input--sm"
                        type="text"
                        id="name"
                        name="patientName"
                        placeholder="Patient Name"
                        onChange={handleFilterChange}
                        value={filters.patientName}
                        autoComplete='off'
                      />
                    </div>
                  </div>
                  <div className='formCol'>
                    <div className="form__Field">
                      <label className="form__label" htmlFor='patientId'>Patient ID</label>
                      <input className="form__input form__input--sm"
                        type="text"
                        id="patientId"
                        name="patientId"
                        placeholder="Patient ID"
                        onChange={handleFilterChange}
                        value={filters.patientId}
                        autoComplete='off'
                      />
                    </div>
                  </div>
                  <div className='formCol'>
                    <div className="form__Field">
                      <label className="form__label" htmlFor='studyDate'>Study Date</label>
                      <div className=''>
                        <DatePickerWithRange
                          id='studyDate'
                          startDate={filters.studyDate.startDate}
                          endDate={filters.studyDate.endDate}
                          onChange={(e) => {
                            handleFilterChange({ isDatePicker: true, values: e })
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className='formRow formRow__3'>
                  <div className='formCol'>
                      <label className="form__label" htmlFor='modalitySelect'>Modality</label>
                      <div className='-mt-2'>
                        <InputMultiSelect
                          id='modalitySelect'
                          key='modalitySelect'
                          value={filters.modality}
                          onChange={(e) => handleFilterChange({ isModality: true, values: e })}
                          options={modalities && modalities.length > 0 ? modalities.map(modality => ({
                            value: modality.value,
                            label: modality.label,
                          })) : []}
                        />
                    </div>
                  </div>
                  <div className='formCol'>
                    <div className="form__Field">
                      <label className="form__label" htmlFor='study'>Study</label>
                      <input className="form__input form__input--sm"
                        type="text"
                        id="study"
                        name="study"
                        placeholder="Study"
                        onChange={handleFilterChange}
                        value={filters.study}
                        autoComplete='off'
                      />
                    </div>
                  </div>
                  <div className='formCol'>
                    <div className="form__Field">
                      <label className="form__label" htmlFor='diagnosis'>Diagnosis</label>
                      <input className="form__input form__input--sm"
                        type="text"
                        id="diagnosis"
                        name="diagnosis"
                        placeholder="Diagnosis"
                        onChange={handleFilterChange}
                        value={filters.diagnosis}
                        autoComplete='off'
                      />
                    </div>
                  </div>
                </div>
                <div className='btn__group'>
                  <button
                    className='btn btn__primary'
                    type="button"
                    onClick={handleSearchClick}
                  >
                    Search
                  </button>
                  <button
                    className='btn btn__white'
                    onClick={handleFilterClear}
                  >
                    Clear
                  </button>
                </div>
                <div className='tableWrap p-0'>
                  {showTable ?
                    studyResearchData?.loading ? <Loader isLoaderOpen={studyResearchData?.loading} /> :
                      <div className='tableContainer px-5 pb-5'>
                        <div>
                          <div className='py-4'>
                            <h2 className="font-semibold text-black">Research Studies</h2>
                          </div>
                          <CustomTable
                            columns={_studyResearchTableColumns}
                            data={studyResearchData?.data?.map((item) => {
                              return {
                                ...item,
                                studyDate: getDateFormat(item?.studyDate).split(' ')[0],
                                dob: formatDate(item?.dob).split(' ')[0],
                              };
                            })}
                            setClickedItem={setClickedItem}
                            clickedItem={clickedItem}
                          />
                        </div>
                      </div>
                    : ''
                  }
                </div>

              </section>
              <section className='form__section'>
                <h1 className='form__section__title'>Study Research Data</h1>
                <div className='p-2 mb-4'  style={{ backgroundColor: '#51A1961F', borderRadius: '12px' }}>
                  <div className='formRow formRow__3'>
                    <div className='formCol'>
                      <label className="form__label" style={{ color: isDarkMode ? '#100F14' : '#100F14' }}>Patient Name</label>
                      <input type="text" value={researchData?.patientName} disabled={true} className='form__input form__input--sm' />
                    </div>
                    <div className='formCol'>
                      <label className="form__label" style={{ color: isDarkMode ? '#100F14' : '#100F14' }}>Patient ID</label>
                      <input type="text" value={researchData?.patientId} disabled={true} className='form__input form__input--sm' />
                    </div>
                    <div className='formCol'>
                      <label className="form__label" style={{ color: isDarkMode ? '#100F14' : '#100F14' }}>Study Date</label>
                      <input type="text" value={researchData?.studyDate} disabled={true} className='form__input form__input--sm' />
                    </div>

                  </div>
                </div>
                <div className='formRow formRow_full' style={{ marginTop: '10px' }}>
                  <div className='formCol'>
                    <div className="form__Field">
                      <label className="form__label" htmlFor='referringDoctorName'>
                        Clinical History
                      </label>
                      <textarea className="form__input form__input--sm"
                        rows={6}
                        // cols={50}
                        id="clinicalHistory"
                        name="clinicalHistory"
                        placeholder="Clinical History"
                        onChange={handleResearchDataChange}
                        value={researchData.clinicalHistory}
                      />
                    </div>
                  </div>
                </div>
                <div className='formRow formRow_full'>
                  <div className='formCol'>
                    <div className="form__Field">
                      <label className="form__label" htmlFor='referringDoctorName'>
                        {`${role == _roleType.pathologist || role === _roleType.technician ? 'Pathologist' : 'Radiologist'} Finding`}
                      </label>
                      <textarea className="form__input form__input--sm"
                        rows={3}
                        cols={50}
                        id="radiologyFinding"
                        name="radiologyFinding"
                        placeholder={`${role == _roleType.pathologist || role === _roleType.technician ? 'Pathologist' : 'Radiologist'} Finding`}
                        onChange={handleResearchDataChange}
                        value={researchData.radiologyFinding}
                      />
                    </div>
                  </div>
                </div>
                <div className='formRow formRow_full'>
                  <div className='formCol'>
                    <div className="form__Field">
                      <label className="form__label" htmlFor='referringDoctorName'>
                        Cytology Findings
                      </label>
                      <textarea className="form__input form__input--sm"
                        rows={3}
                        cols={50}
                        id="cytologyFindings"
                        name="cytologyFindings"
                        placeholder="Cytology Findings"
                        onChange={handleResearchDataChange}
                        value={researchData.cytologyFindings}
                      />
                    </div>
                  </div>
                </div>
                <div className='formRow formRow_full'>
                  <div className='formCol'>
                    <div className="form__Field">
                      <label className="form__label" htmlFor='referringDoctorName'>
                        Diagnosis
                      </label>
                      <textarea className="form__input form__input--sm"
                        rows={3}
                        cols={50}
                        id="diagnosis"
                        name="diagnosis"
                        placeholder="Diagnosis"
                        onChange={handleResearchDataChange}
                        value={researchData.diagnosis}
                      />
                    </div>
                  </div>
                </div>
                <div className='formRow formRow_full'>
                  <div className='formCol'>
                    <div className="form__Field">
                      <label className="form__label" htmlFor='referringDoctorName'>
                        Follow Up

                      </label>
                      <textarea className="form__input form__input--sm"
                        rows={3}
                        cols={50}
                        id="followUp"
                        name="followUp"
                        placeholder="Follow Up"
                        onChange={handleResearchDataChange}
                        value={researchData.followUp}
                      />
                    </div>
                  </div>
                </div>
                <div className='formRow formRow_full'>
                  <div className='formCol'>
                    <div className="form__Field">
                      <label className="form__label" htmlFor='referringDoctorName'>
                        Discussion Notes

                      </label>
                      <textarea className="form__input form__input--sm"
                        rows={3}
                        cols={50}
                        id="discussionNotes"
                        name="discussionNotes"
                        placeholder="Discussion Notes"
                        onChange={handleResearchDataChange}
                        value={researchData.discussionNotes}
                      />
                    </div>
                  </div>
                </div>
                <div className='formRow formRow_full'>
                  <div className='formCol'>
                    <div className="form__Field">
                      <label className="form__label" htmlFor='referringDoctorName'>
                        Final Treatment

                      </label>
                      <textarea className="form__input form__input--sm"
                        rows={3}
                        cols={50}
                        id="finalTreatment"
                        name="finalTreatment"
                        placeholder="Final Treatment"
                        onChange={handleResearchDataChange}
                        value={researchData.finalTreatment}
                      />
                    </div>
                  </div>
                  <div className='btn__group'>
                    <button
                      className='btn btn__primary'
                      type="button"
                      onClick={handleSaveClick}
                    >
                      Save
                    </button>
                    <button
                      className='btn btn__white'
                      onClick={handleCancelClick}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </section>
              <section className='form__section'>
                <div className='btn__group btn__group--end'>
                  <button className='btn btn__primary'
                    onClick={handleViewerClick}
                  >
                    Image Viewer
                  </button>
                  {/* <div className='ml-1 cursor-pointer' onClick={handleNotesClick}>Notes</div> */}
                </div>
              </section>
            </div>
          </div>
        </div>

        <Loader isLoaderOpen={updateStudyResearchResp.loading} />
        <SuccessModal
          isSuccessModalOpen={successPopup}
          title={'Data saved successfully.'}
          handleCloseModal={() => setSuccessPopup(false)}
        />
      </div>
    </>
  )
}

export default StudyResearch
